/**
 * SavedViewsManager — F12.E T12.E.5.
 *
 * Zarządzanie zapisanymi widokami listy rekordów.
 * Przechowywane w localStorage per moduł (klucz: `saved-views-<moduleId>`).
 *
 * Widok obejmuje: filters + sort + groupBy + viewMode + columnPicker (automatycznie).
 *
 * Komponenty:
 *  - `SavedViewsManager` — przycisk + dropdown save/load/delete
 *  - `useSavedViews`     — hook CRUD dla localStorage
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkCheck, Check, ChevronDown, Plus, Trash2, X } from 'lucide-react';

import type { ActiveFilter, GroupBySlug, SortConfig } from './RecordFiltersBar';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SavedViewConfig {
  filters: ActiveFilter[];
  sort: SortConfig | null;
  groupBy: GroupBySlug | null;
  viewMode: 'table' | 'kanban' | 'gallery' | 'calendar';
}

export interface SavedView {
  id: string;
  name: string;
  config: SavedViewConfig;
  createdAt: string;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

function storageKey(moduleId: string) {
  return `saved-views-${moduleId}`;
}

function loadViews(moduleId: string): SavedView[] {
  if (!moduleId) return [];
  try {
    const raw = localStorage.getItem(storageKey(moduleId));
    if (raw) return JSON.parse(raw) as SavedView[];
  } catch {
    // ignore
  }
  return [];
}

function saveViews(moduleId: string, views: SavedView[]) {
  try {
    localStorage.setItem(storageKey(moduleId), JSON.stringify(views));
  } catch {
    // ignore
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSavedViews(moduleId: string) {
  const [views, setViews] = useState<SavedView[]>(() => loadViews(moduleId));

  // Sync when moduleId changes (navigation between modules)
  useEffect(() => {
    setViews(loadViews(moduleId));
  }, [moduleId]);

  function saveView(name: string, config: SavedViewConfig) {
    const next: SavedView = {
      id: crypto.randomUUID(),
      name,
      config,
      createdAt: new Date().toISOString(),
    };
    setViews((prev) => {
      const updated = [...prev, next];
      saveViews(moduleId, updated);
      return updated;
    });
    return next;
  }

  function deleteView(id: string) {
    setViews((prev) => {
      const updated = prev.filter((v) => v.id !== id);
      saveViews(moduleId, updated);
      return updated;
    });
  }

  return { views, saveView, deleteView };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  /**
   * Klucz storage (dla legacy localStorage hook). Gdy `controlled` przekazuje
   * own views/saveView/deleteView — moduleId służy tylko jako test id prefix
   * (i pomija się storage).
   */
  moduleId: string;
  currentConfig: SavedViewConfig;
  onLoad: (config: SavedViewConfig) => void;
  /**
   * F12 PR2 cz.5.2 etap 5 — opcjonalny controlled mode dla backend storage.
   * Gdy podany — komponent NIE używa `useSavedViews` (localStorage). Caller
   * przekazuje views z `useSavedListingViews` (backend).
   */
  controlledViews?: SavedView[];
  controlledSaveView?: (name: string, config: SavedViewConfig) => void;
  controlledDeleteView?: (id: string) => void;
}

export function SavedViewsManager({
  moduleId,
  currentConfig,
  onLoad,
  controlledViews,
  controlledSaveView,
  controlledDeleteView,
}: Props) {
  const { t } = useTranslation();
  const legacy = useSavedViews(moduleId);
  // Controlled mode: caller dostarcza views/saveView/deleteView.
  const isControlled = controlledViews !== undefined;
  const views = isControlled ? controlledViews! : legacy.views;
  const saveView = isControlled
    ? (name: string, config: SavedViewConfig) => controlledSaveView?.(name, config)
    : legacy.saveView;
  const deleteView = isControlled ? (id: string) => controlledDeleteView?.(id) : legacy.deleteView;
  const [open, setOpen] = useState(false);
  const [saveMode, setSaveMode] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Zamknij kliknięciem poza panelem
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSaveMode(false);
        setNameInput('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus input gdy wchodzi tryb save
  useEffect(() => {
    if (saveMode) inputRef.current?.focus();
  }, [saveMode]);

  function handleSave() {
    const name = nameInput.trim();
    if (!name) return;
    saveView(name, currentConfig);
    setSaveMode(false);
    setNameInput('');
    setOpen(false);
  }

  function handleLoad(view: SavedView) {
    onLoad(view.config);
    setLoadedId(view.id);
    setOpen(false);
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setSaveMode(false);
          setNameInput('');
        }}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
          open
            ? 'border-accent-500 bg-accent-500/10 text-accent-600'
            : 'border-border-primary bg-bg-primary text-text-secondary hover:text-text-primary'
        }`}
        data-testid="saved-views-btn"
        title={t('tenant.modules.savedViews.title')}
      >
        <BookmarkCheck className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t('tenant.modules.savedViews.title')}</span>
        {views.length > 0 && (
          <span className="rounded-full bg-accent-500 px-1 py-0.5 text-xs font-medium text-white leading-none">
            {views.length}
          </span>
        )}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1 w-64 rounded-xl border border-border-primary bg-bg-primary shadow-lg"
          data-testid="saved-views-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border-primary px-3 py-2">
            <span className="text-sm font-medium text-text-primary">
              {t('tenant.modules.savedViews.title')}
            </span>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setSaveMode(false);
                setNameInput('');
              }}
              className="text-text-muted hover:text-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Lista zapisanych widoków */}
          <div className="max-h-52 overflow-y-auto py-1">
            {views.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs text-text-muted">
                {t('tenant.modules.savedViews.empty')}
              </p>
            ) : (
              views.map((view) => (
                <div
                  key={view.id}
                  className="group flex items-center gap-1 px-2 py-1.5 hover:bg-bg-secondary rounded-md mx-1"
                >
                  <button
                    type="button"
                    onClick={() => handleLoad(view)}
                    className="flex flex-1 items-center gap-2 min-w-0 text-left"
                    title={t('tenant.modules.savedViews.load')}
                    data-testid={`saved-view-load-${view.id}`}
                  >
                    {loadedId === view.id ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-success-500" />
                    ) : (
                      <BookmarkCheck className="h-3.5 w-3.5 shrink-0 text-text-muted group-hover:text-accent-500" />
                    )}
                    <span className="truncate text-sm text-text-primary">{view.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteView(view.id);
                    }}
                    className="shrink-0 rounded p-0.5 text-text-muted opacity-0 hover:text-error-500 group-hover:opacity-100 transition-opacity"
                    title={t('tenant.modules.savedViews.delete')}
                    data-testid={`saved-view-delete-${view.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Save current state */}
          <div className="border-t border-border-primary px-3 py-2">
            {saveMode ? (
              <div className="flex items-center gap-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') {
                      // „Obsłużone" — okno, w którym stoi menedżer widoków, się nie zamyka.
                      e.preventDefault();
                      setSaveMode(false);
                      setNameInput('');
                    }
                  }}
                  placeholder={t('tenant.modules.savedViews.namePlaceholder')}
                  className="flex-1 rounded border border-border-primary bg-bg-primary px-2 py-1 text-xs focus:border-accent-500 focus:outline-none"
                  maxLength={60}
                  data-testid="saved-view-name-input"
                />
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!nameInput.trim()}
                  className="rounded-md bg-accent-500 px-2 py-1 text-xs text-white disabled:opacity-50"
                  data-testid="saved-view-save-confirm-btn"
                >
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSaveMode(false);
                    setNameInput('');
                  }}
                  className="rounded-md px-1 py-1 text-text-muted hover:text-text-primary"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setSaveMode(true)}
                className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary"
                data-testid="saved-view-save-btn"
              >
                <Plus className="h-3 w-3" />
                {t('tenant.modules.savedViews.saveCurrentView')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Settings panel ───────────────────────────────────────────────────────────

/**
 * SavedViewsSettingsPanel — wyświetla zapisane widoki modułu w zakładce Filtry
 * w ModuleSettingsPage. Pozwala je przeglądać i usuwać.
 */
export function SavedViewsSettingsPanel({ moduleId }: { moduleId: string }) {
  const { t } = useTranslation();
  const { views, deleteView } = useSavedViews(moduleId);

  if (views.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-text-muted">
        <BookmarkCheck className="mx-auto mb-3 h-8 w-8 opacity-30" />
        <p>{t('tenant.modules.savedViews.settingsEmpty')}</p>
        <p className="mt-1 text-xs">{t('tenant.modules.savedViews.settingsEmptyHint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 py-2">
      <p className="text-xs text-text-muted mb-3">
        {t('tenant.modules.savedViews.settingsDescription')}
      </p>
      {views.map((view) => (
        <div
          key={view.id}
          className="flex items-center justify-between rounded-lg border border-border-primary bg-bg-primary px-4 py-3"
        >
          <div>
            <p className="text-sm font-medium text-text-primary">{view.name}</p>
            <p className="text-xs text-text-muted">
              {t('tenant.modules.savedViews.viewMode')}: {view.config.viewMode}
              {view.config.filters.length > 0 &&
                ` · ${view.config.filters.length} ${t('tenant.modules.savedViews.filtersCount')}`}
              {view.config.sort && ` · ${t('tenant.modules.savedViews.hasSort')}`}
              {view.config.groupBy && ` · ${t('tenant.modules.savedViews.hasGroupBy')}`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => deleteView(view.id)}
            className="ml-4 rounded p-1.5 text-text-muted hover:bg-error-50 hover:text-error-500 dark:hover:bg-error-900/20"
            title={t('tenant.modules.savedViews.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
